import { Component, Input, OnInit } from "@angular/core"
import { ActivatedRoute } from "@angular/router"
import { FacetingService } from "../../../services/faceting.service"
import { SearchingService } from "../../../services/searching.service"
import { facetType } from "../../../utils/types"
import { EduComponent } from "../../../utils/eduComponent"
import Utils from "../../../utils/utils"
import { fadeInXY, rotateRight } from "../../../utils/animations"

@Component({
    selector: "app-generic-facet",
    templateUrl: "./generic-facet.component.html",
    styleUrls: ["./generic-facet.component.scss"],
    animations: [fadeInXY, rotateRight],
    standalone: false,
})
export class GenericFacetComponent extends EduComponent implements OnInit {
    @Input() dynamicFacetName: string = ""
    @Input() facetName: string = ""
    @Input() layout: string = ""
    @Input() isCollapsed: boolean = true
    @Input() syncFacets: boolean = false
    @Input() keyOrder: String[] = null
    @Input() defaultOptions: String[] = null
    @Input() staticOptions: boolean = false
    @Input() allowedFacetKeys: String[] = null // null for all

    options: any[] = []
    // For padding if the options number does not divide correctly
    fakeOptions: any[] = []
    title: string = ""
    selectedFiltersCount: number = 0
    isStudentExperience: boolean = false

    constructor(
        public facetingService: FacetingService,
        public searchingService: SearchingService,
        public route: ActivatedRoute
    ) {
        super()
    }

    public ngOnInit() {
        this.isStudentExperience = Utils.hasStudentExperience()
        if (this.dynamicFacetName) {
            // If this is a dynamic facet, it will get both its display title and values
            // whenever they change from the faceting service
            this.addSub(
                this.facetingService[`${this.dynamicFacetName}_title`].subscribe((title) => {
                    this.title = title
                })
            )
            this.addSub(
                this.facetingService[`${this.dynamicFacetName}`].subscribe((facet) => {
                    this.setOptions(facet)
                })
            )
        } else {
            // If this isn't a dynamic facet, it will get its title only once
            // from the faceting service.
            this.title = this.facetingService[`${this.facetName}_title`]

            if (!this.staticOptions) {
                // there are currently 2 availableFacetsChanged subscribers, here and in the facetService
                // the one from the facetService is trusted that will always trigger first so that the options can be populated
                this.setOptions(this.facetingService[`${this.facetName}`])

                this.addSub(
                    this.searchingService.availableFacetsChanged.subscribe((data) => {
                        this.setOptions(this.facetingService[`${this.facetName}`])
                    })
                )
            } else {
                this.options = this.defaultOptions.map((label) => ({
                    label,
                    checked: false,
                }))
            }
        }
        this.addSub(
            this.facetingService.filtersChanged.subscribe((selectedFilters) => {
                this.selectOptions(selectedFilters)
                this.selectedFiltersCount = selectedFilters.filter(
                    (x) => x.split(":")[0] === this.facetName
                ).length
            })
        )

        this.synchronizeFacets()
        this.updateGridPaddingOptions()
    }

    public selectOptions(selectedFilters: string[]) {
        this.options.map((option) => {
            option.checked = selectedFilters.indexOf(this.getFilterName(option)) !== -1
            this.isCollapsed = !option.checked && this.isCollapsed
        })
    }

    public setOptions(options: any[]) {
        let newOptions = options.slice()
        if (this.allowedFacetKeys) {
            newOptions = newOptions.filter(
                (facet) => this.allowedFacetKeys.indexOf(facet.label) !== -1
            )
        }
        if (this.keyOrder) {
            newOptions.sort((a, b) => {
                return this.keyOrder.indexOf(a.label) - this.keyOrder.indexOf(b.label)
            })
        }
        this.options = newOptions
        this.updateGridPaddingOptions()
        this.selectOptions(this.facetingService.appliedFilters)
    }

    public updateGridPaddingOptions() {
        if (this.layout !== "grid") {
            this.fakeOptions = []
            return
        }
        const lastRowCount = this.options.length % 3
        if (lastRowCount === 0) {
            this.fakeOptions = []
        } else if (lastRowCount === 1) {
            this.fakeOptions = ["", ""]
        } else if (lastRowCount === 2) {
            this.fakeOptions = [""]
        }
    }

    public onCheckboxChange(event, option) {
        option.checked = !option.checked

        if (event.target.checked) {
            this.facetingService.addFilter(this.getFilterName(option), this.route)
        } else {
            this.facetingService.removeFilter(this.getFilterName(option), this.route)
        }
    }

    public onToggle() {
        this.isCollapsed = !this.isCollapsed
        if (this.syncFacets) {
            this.facetingService.broadcastOnToggle(this.isCollapsed, this.facetName)
        }
    }

    protected getFilterName(option) {
        const facetName = this.dynamicFacetName || this.facetName
        if (option.id) {
            return `${facetName}:${option.id}`
        }
        return `${facetName}:${option.label.replace(/\s/g, " ")}`
    }

    protected synchronizeFacets() {
        if (!this.syncFacets) return

        this.isCollapsed = true
        this.addSub(
            this.facetingService.toggleFilter.subscribe((facetType: facetType) => {
                if (facetType.type !== this.facetName) {
                    this.isCollapsed = true
                }
            })
        )
    }
}
